import React, { useState, useEffect } from 'react';
import {
  BarChart,
  BookOpen,
  FileText,
  Book,
  Search,
  Edit3,
  ClipboardCheck
} from 'lucide-react';
import { Link } from 'react-router-dom';
import * as api from '../../../api/educentialApi';

const TestPrepDashboard = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [testsRes, enrollmentsRes] = await Promise.all([
        api.getAvailableTests(),
        api.getUserTestEnrollments(),
      ]);
      setTests(testsRes.data || []);
      setEnrollments(enrollmentsRes.data || []);
    } catch (err) {
      console.error('Error fetching test prep data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Test Preparation</h1>
              <p className="text-gray-600 mt-1">Prepare for IELTS, TOEFL, GRE, GMAT and more</p>
            </div>
            <Link
              to="/services"
              className="px-4 py-2 text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
            >
              ← Back to Services
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard label="Tests Enrolled" value={enrollments.length} icon={FileText} />
          <StatCard label="Practice Tests" value={enrollments.reduce((sum, e) => sum + (e.practiceTestsTaken || 0), 0)} icon={Edit3} />
          <StatCard
            label="Avg. Score"
            value={
              enrollments.length > 0 && enrollments.some(e => e.averageScore)
                ? `${Math.round(enrollments.reduce((sum, e) => sum + (e.averageScore || 0), 0) / enrollments.filter(e => e.averageScore).length)}%`
                : 'N/A'
            }
            icon={BarChart}
          />
          <StatCard label="Available Tests" value={tests.length} icon={BookOpen} />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ActionCard
              title="Browse Tests"
              description="Explore available tests"
              icon={Search}
              link="/services/test-prep/catalog"
            />
            <ActionCard
              title="My Tests"
              description="View enrolled tests"
              icon={Book}
              link="/services/test-prep/my-tests"
            />
            <ActionCard
              title="Practice Tests"
              description="Take practice exams"
              icon={ClipboardCheck}
              link="/services/test-prep/practice"
            />
          </div>
        </div>

        {/* Enrolled Tests */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">My Enrolled Tests</h2>
            <Link to="/services/test-prep/my-tests" className="text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors">
              View All →
            </Link>
          </div>
          {enrollments.length > 0 ? (
            <div className="space-y-3">
              {enrollments.map((enrollment) => (
                <div key={enrollment.testId} className="group border border-gray-200 rounded-lg p-4 hover:border-teal-200 hover:bg-teal-50/30 transition-all">
                  <h3 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors mb-1">{enrollment.test?.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{enrollment.test?.description}</p>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">
                      Practice tests taken: <span className="font-medium text-gray-900">{enrollment.practiceTestsTaken || 0}</span>
                    </span>
                    {enrollment.averageScore && (
                      <span className="text-teal-600 font-semibold">
                        Avg. Score: {Math.round(enrollment.averageScore)}%
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 mb-3">No enrolled tests yet</p>
              <Link to="/services/test-prep/catalog" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium">
                Browse test catalog →
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon }) => {
  return (
    <div className="relative bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 overflow-hidden group">
      <div className="absolute inset-0 bg-teal-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="bg-teal-100 p-3 rounded-lg">
            <Icon className="w-6 h-6 text-teal-600" />
          </div>
        </div>
        <p className="text-sm text-gray-600 font-medium">{label}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
    </div>
  );
};

const ActionCard = ({ title, description, icon: Icon, link }) => {
  return (
    <Link
      to={link}
      className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 hover:border-teal-200"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="bg-teal-100 p-3 rounded-lg group-hover:bg-teal-600 transition-colors">
          <Icon className="w-6 h-6 text-teal-600 group-hover:text-white transition-colors" />
        </div>
      </div>
      <h3 className="font-semibold text-lg mb-1 text-gray-900 group-hover:text-teal-600 transition-colors">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </Link>
  );
};

export default TestPrepDashboard;
